import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  followUserUseCase,
  getFollowersUseCase,
  unfollowUserUseCase,
} from '@/src/core/dependencies';
import { FollowWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { FollowersScreen } from '@/src/presentation/screens/FollowersScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function FollowersContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const { user } = useAuth();
  const [followers, setFollowers] = useState<FollowWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());

  useFocusEffect(
    useCallback(() => {
      loadFollowers();
    }, [userId])
  );

  const loadFollowers = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getFollowersUseCase.execute(userId, PAGE_SIZE, 0);
      setFollowers(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Followers', 'Failed to load followers', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFollowers = async () => {
    if (loadingMore || !hasMore || !userId) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getFollowersUseCase.execute(userId, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setFollowers((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Followers', 'Failed to load more followers', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFollowToggle = async (followerId: string) => {
    if (!user) return;

    try {
      const isFollowing = followingIds.has(followerId);

      if (isFollowing) {
        await unfollowUserUseCase.execute(user.id, followerId);
        setFollowingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(followerId);
          return newSet;
        });
      } else {
        await followUserUseCase.execute(user.id, followerId);
        setFollowingIds((prev) => new Set(prev).add(followerId));
      }
    } catch (error) {
      logger.error('Followers', 'Failed to toggle follow', { error });
      platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
    }
  };

  const handleUserPress = (followerId: string) => {
    router.push(`/profile/${followerId}`);
  };

  return (
    <FollowersScreen
      followers={followers}
      loading={loading}
      loadingMore={loadingMore}
      currentUserId={user?.id}
      followingIds={followingIds}
      onFollowToggle={handleFollowToggle}
      onUserPress={handleUserPress}
      onRefresh={loadFollowers}
      onLoadMore={loadMoreFollowers}
      colors={colors}
    />
  );
}
